import React, { useState, useEffect } from 'react';
import CRUDList from '../Common/CRUDList';
import { routes, airportPairs, airports } from '../../services/api';

const directionLabels = { 1: 'Forward', 2: 'Reverse' };

function RouteForm({ formData, setFormData }) {
  const [pairsList, setPairsList] = useState([]);
  const [airportsList, setAirportsList] = useState([]);
  const [error, setError] = useState('');

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [pairsRes, airportsRes] = await Promise.all([
        airportPairs.getAll(),
        airports.getAll()
      ]);
      setPairsList(pairsRes.data);
      setAirportsList(airportsRes.data);
    } catch (err) {
      console.error('Failed to load data', err);
    }
  };

  // Find pair and direction based on selected airports
  const findPairAndDirection = (depAirportId, arrAirportId) => {
    if (!depAirportId || !arrAirportId) {
      setError('');
      return { route_apair_id: null, route_direction: null };
    }

    // Find pair where airports match in either order
    const pair = pairsList.find(p =>
      (p.apair_airport1_id === depAirportId && p.apair_airport2_id === arrAirportId) ||
      (p.apair_airport1_id === arrAirportId && p.apair_airport2_id === depAirportId)
    );

    if (!pair) {
      setError('No airport pair exists for these airports. Please create the pair first.');
      return { route_apair_id: null, route_direction: null };
    }

    setError('');
    // Direction 1 (Forward): airport1 -> airport2
    // Direction 2 (Reverse): airport2 -> airport1
    const direction = pair.apair_airport1_id === depAirportId ? 1 : 2;
    return { route_apair_id: pair.apair_id, route_direction: direction };
  };

  const handleDepAirportChange = (depAirportId) => {
    const { route_apair_id, route_direction } = findPairAndDirection(depAirportId, formData.arr_airport_id);
    setFormData({
      ...formData,
      dep_airport_id: depAirportId,
      route_apair_id,
      route_direction
    });
  };

  const handleArrAirportChange = (arrAirportId) => {
    const { route_apair_id, route_direction } = findPairAndDirection(formData.dep_airport_id, arrAirportId);
    setFormData({
      ...formData,
      arr_airport_id: arrAirportId,
      route_apair_id,
      route_direction
    });
  };

  const selectedPair = pairsList.find(p => p.apair_id === formData.route_apair_id);

  return (
    <>
      <div className="form-group">
        <label>Departure Airport *</label>
        <select
          value={formData.dep_airport_id || ''}
          onChange={(e) => handleDepAirportChange(e.target.value ? parseInt(e.target.value) : null)}
          required
        >
          <option value="">Select departure airport</option>
          {airportsList.map(airport => (
            <option key={airport.airport_id} value={airport.airport_id}>
              {airport.airport_iata_code} ({airport.airport_icao_code})
            </option>
          ))}
        </select>
      </div>

      <div className="form-group">
        <label>Arrival Airport *</label>
        <select
          value={formData.arr_airport_id || ''}
          onChange={(e) => handleArrAirportChange(e.target.value ? parseInt(e.target.value) : null)}
          required
        >
          <option value="">Select arrival airport</option>
          {airportsList.map(airport => (
            <option key={airport.airport_id} value={airport.airport_id}>
              {airport.airport_iata_code} ({airport.airport_icao_code})
            </option>
          ))}
        </select>
      </div>

      {error && (
        <div className="form-group">
          <span style={{ color: 'red' }}>{error}</span>
        </div>
      )}

      {selectedPair && (
        <div className="form-group">
          <label>Detected Pair & Direction</label>
          <input
            type="text"
            value={`${selectedPair.airport1_iata} ↔ ${selectedPair.airport2_iata} (${directionLabels[formData.route_direction]})`}
            disabled
          />
        </div>
      )}
    </>
  );
}

// Format distance for display (convert meters to km)
const formatDistance = (meters) => {
  if (!meters) return '-';
  const km = meters / 1000;
  return `${km.toLocaleString()} km`;
};

function RoutesList() {
  return (
    <CRUDList
      title="Routes"
      api={routes}
      columns={['Route', 'ID', 'Airport Pair', 'Direction', 'Distance']}
      FormComponent={RouteForm}
      defaultFormData={{ dep_airport_id: '', arr_airport_id: '', route_apair_id: '', route_direction: '' }}
      useOldStyle={true}
      renderRow={(item) => (
        <>
          <td>{item.dep_iata} → {item.arr_iata}</td>
          <td>{item.route_id}</td>
          <td>{item.airport1_iata} ↔ {item.airport2_iata}</td>
          <td><span className="badge badge-info">{directionLabels[item.route_direction]}</span></td>
          <td>{formatDistance(item.apair_distance)}</td>
        </>
      )}
    />
  );
}

export default RoutesList;
